import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  CircleDotDashed,
  Copy,
  HandCoins,
  Pin,
  UsersRound,
  Wallet,
} from "lucide-react";
import Button from "../../../../components/ui/buttons/Button";
import CustomActionButton from "../../../../components/ui/buttons/CustomActionButton";
import DashboardTab from "../../../planner/client/dashboard/DashboardTab";
import InvestmentFunds from "./tabs/InvestmentFunds";
import PlannerSelect from "../../../../components/ui/selects/PlannerSelect";
import { cn } from "../../../../utils/classnames";
import CustomSelect from "../../../../components/ui/selects/CustomSelect";
import { fetchWithCsrf } from "../../../../utils/fetchWithCsrf";
import { camelizeKeys } from "../../../../utils/camelizeKeys";
import SmallToggle from "../../../../components/ui/toggles/SmallToggle";
import FixedIncome from "./tabs/FixedIncome";
import Pension from "./tabs/Pension";
import Equities from "./tabs/Equities";
import CurrencyText from "../../../../components/ui/labels/CurrencyText";
import DonutChart3 from "../../../../components/ui/charts/DonutChart3";
import { useToast } from "../../../../components/ui/toast/ToastContext";
import Statement from "./tabs/Statement";
import History from "./tabs/History";

const ScoreBadge = ({ score, tone }) => {
  const toneStyles = {
    success: {
      containerBg: "bg-blueLightest",
      dotColor: "bg-tealDeep",
    },
    danger: {
      containerBg: "bg-redExtraLight",
      dotColor: "bg-redDeep",
    },
    neutral: {
      containerBg: "bg-yellowExtraLight",
      dotColor: "bg-yellowDark",
    },
    none: {
      containerBg: "bg-tealTransparent",
      dotColor: "bg-blueMediumDark",
    },
  };

  const style = toneStyles[tone] || toneStyles.neutral;

  return (
    <span
      className={`relative text-blueDarker inline-flex ${style.containerBg} text-base font-medium px-3 py-1 flex-row items-center gap-2 transition-opacity`}
      style={{ borderRadius: "10px" }}
    >
      <span
        className={`flex-none block rounded ${style.dotColor}`}
        style={{
          width: "12px",
          height: "12px",
        }}
      />
      <span className="font-semibold">
        Nota: {score || 0}
        <span className="font-normal">/100</span>
      </span>
    </span>
  );
};

const ClientDashboard = ({
  apiClient: initialApiClient,
  accountOptions: initialAccountOptions,
  walletProfileOptions: initialWalletProfileOptions,
  investmentWalletOptions: initialInvestmentWalletOptions,
  pensionWalletOptions: initialPensionWalletOptions,
  offshoreWalletOptions: initialOffshoreWalletOptions,
  sidebarItems,
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get('from');

  const [apiClient, setApiClient] = useState(initialApiClient);
  const [accountOptions, setAccountOptions] = useState(initialAccountOptions);
  const [walletProfileOptions, setWalletProfileOptions] = useState(initialWalletProfileOptions);
  const [investmentWalletOptions, setInvestmentWalletOptions] = useState(initialInvestmentWalletOptions);
  const [pensionWalletOptions, setPensionWalletOptions] = useState(initialPensionWalletOptions);
  const [offshoreWalletOptions, setOffshoreWalletOptions] = useState(initialOffshoreWalletOptions);
  const [selectedNoteType, setSelectedNoteType] = useState('INVESTIMENTOS');

  const { addToast } = useToast();

  const [walletForm, setWalletForm] = useState({
    walletProfileId: initialApiClient.walletProfileId || '',
    investmentWalletId: initialApiClient.investmentWalletId || '',
    pensionWalletId: initialApiClient.pensionWalletId || '',
    offshoreWalletId: initialApiClient.offshoreWalletId || '',
  });

  const [selectedAccount, setSelectedAccount] = useState(initialApiClient.id);
  const [accountCopied, setAccountCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const tabs = [
    { key: "investment_funds", label: "FUNDOS" },
    { key: "fixed_income", label: "RENDA FIXA" },
    { key: "equities", label: "RENDA VARIÁVEL" },
    { key: "pension", label: "PREVIDÊNCIA" },
    { key: "statement", label: "EXTRATO" },
    { key: "history", label: "HISTÓRICO" },
  ];

  const validTabKeys = tabs.map((t) => t.key);

  const [activeTab, setActiveTab] = useState(() => {
    const url = new URL(window.location.href);
    const tabFromUrl = url.searchParams.get("tab");

    if (tabFromUrl && validTabKeys.includes(tabFromUrl)) return tabFromUrl;

    return tabs[0].key;
  });

  useEffect(() => {
    setApiClient(initialApiClient);
    setAccountOptions(initialAccountOptions);
    setWalletProfileOptions(initialWalletProfileOptions);
    setInvestmentWalletOptions(initialInvestmentWalletOptions);
    setPensionWalletOptions(initialPensionWalletOptions);
    setOffshoreWalletOptions(initialOffshoreWalletOptions);
    setSelectedAccount(initialApiClient.id);

    setWalletForm({
      walletProfileId: initialApiClient.walletProfileId || '',
      investmentWalletId: initialApiClient.investmentWalletId || '',
      pensionWalletId: initialApiClient.pensionWalletId || '',
      offshoreWalletId: initialApiClient.offshoreWalletId || '',
    });
  }, [
    initialApiClient,
    initialAccountOptions,
    initialWalletProfileOptions,
    initialInvestmentWalletOptions,
    initialPensionWalletOptions,
    initialOffshoreWalletOptions,
  ]);

  const applyDashboardData = (dashboardData) => {
    setApiClient(dashboardData.apiClient);
    setAccountOptions(dashboardData.accountOptions || []);
    setWalletProfileOptions(dashboardData.walletProfileOptions || []);
    setInvestmentWalletOptions(dashboardData.investmentWalletOptions || []);
    setPensionWalletOptions(dashboardData.pensionWalletOptions || []);
    setOffshoreWalletOptions(dashboardData.offshoreWalletOptions || []);

    setWalletForm({
      walletProfileId: dashboardData.apiClient.walletProfileId || '',
      investmentWalletId: dashboardData.apiClient.investmentWalletId || '',
      pensionWalletId: dashboardData.apiClient.pensionWalletId || '',
      offshoreWalletId: dashboardData.apiClient.offshoreWalletId || '',
    });

    setSelectedAccount(dashboardData.apiClient.id);
  };

  const fetchData = async () => {
    try {
      const res = await fetch(
        `/app/analyst/client/${apiClient.id}/dashboard.json`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(
          responseData.message || "Erro ao recarregar dados da dashboard",
        );
      }

      const data = camelizeKeys(responseData);
      console.log("Dados atualizados da dashboard:", data);

      applyDashboardData(data);
    } catch (err) {
      console.error(err);
      addToast({
        type: "error",
        message: err.message || "Erro ao recarregar dados da dashboard",
      });
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);

    const url = new URL(window.location);
    url.searchParams.set("tab", key);
    window.history.pushState({}, "", url);
  };

  const handleAccountChange = (accountId) => {
    window.location.href = `/app/analyst/client/${accountId}/dashboard?from=operations.clients`;
  };

  const copyBrokerIdToClipboard = () => {
    if (!apiClient.brokerId) return;

    navigator.clipboard
      .writeText(apiClient.brokerId)
      .then(() => {
        setAccountCopied(true);
        setTimeout(() => setAccountCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Erro ao copiar conta:", err);
      });
  };

  const handleWalletChange = async (e) => {
    const { name, value } = e.target;

    const parsedValue = value === "" ? null : Number(value);

    const nextForm = {
      ...walletForm,
      [name]: parsedValue || "",
    };

    if (name === 'walletProfileId') {
      nextForm.investmentWalletId = '';
      nextForm.pensionWalletId = '';
      nextForm.offshoreWalletId = '';
      setInvestmentWalletOptions([]);
      setPensionWalletOptions([]);
      setOffshoreWalletOptions([]);
    }

    setWalletForm(nextForm);
    setLoading(true);

    const fieldName =
      name === "walletProfileId"
        ? "wallet_profile_id"
        : name === "investmentWalletId"
          ? "investment_wallet_id"
          : "pension_wallet_id";

    const payload = {
      api_client: {
        [fieldName]: parsedValue,
      },
    };

    try {
      const res = await fetchWithCsrf(
        `/app/analyst/client/${apiClient.id}/dashboard`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(responseData.message || "Erro ao atualizar carteira");
      }

      const data = camelizeKeys(responseData);

      applyDashboardData(data);

      addToast({
        type: "success",
        message: "Carteira atualizada com sucesso!",
      });
    } catch (err) {
      console.error(err);

      setWalletForm({
        walletProfileId: apiClient.walletProfileId || '',
        investmentWalletId: apiClient.investmentWalletId || '',
        pensionWalletId: apiClient.pensionWalletId || '',
        offshoreWalletId: apiClient.offshoreWalletId || '',
      });

      addToast({
        type: "error",
        message: err.message || "Erro ao atualizar carteira",
      });
    } finally {
      setLoading(false);
    }
  };

  const statusColor =
    apiClient.client.status === "active"
      ? "text-blueHighlight"
      : "text-redBase";

  const generalScore =
    apiClient.balancingStatus && apiClient.balancingStatus.general;
  const investmentScore =
    apiClient.balancingStatus && apiClient.balancingStatus.investment;
  const pensionScore =
    apiClient.balancingStatus && apiClient.balancingStatus.pension;

  return (
    <div className="p-5 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <span className="flex items-center gap-2">
          <UsersRound className="text-tealMedium" size={20} strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-blueDarker">Clientes</h1>
        </span>

        <div className="flex items-center gap-3">
          <Button
            icon={ChevronLeft}
            onClick={() => {
              const targetHref = sidebarItems.find((item) => item.featureKey === from)?.href || '/app/analyst/clients';
              window.location.href = targetHref;
            }}
          >
            VOLTAR
          </Button>
        </div>
      </div>

      <div
        className="grid gap-5 h-full"
        style={{ gridTemplateColumns: "minmax(0, 1fr) 420px" }}
      >
        <div className="min-w-0 flex flex-col gap-5">
          <div className="bg-white rounded-custom20 p-8 flex flex-col gap-8">
            <div className="flex flex-row items-center gap-3 justify-between">
              <h2 className="text-xl font-semibold">{apiClient.client.name}</h2>

              <div className="flex items-center flex-row gap-4">
                <PlannerSelect
                  options={accountOptions}
                  label="CONTA"
                  value={selectedAccount}
                  onChange={(e) => handleAccountChange(e.target.value)}
                />

                <div
                  className={cn(
                    "flex items-center gap-1 border border-bluePale rounded-full px-5 py-2 bg-white",
                  )}
                >
                  <span className="text-xs text-blueMedium font-semibold tracking-wide">
                    PLANEJADOR:
                  </span>
                  <span className="text-xs text-blueMedium font-normal mr-2">
                    {apiClient.client.planner.name.toUpperCase()}
                  </span>
                </div>

                {apiClient.client.accessLevel && (
                  <a href={`/app/planner/client/${apiClient.client.id}/profile?from=analyst_client_${apiClient.id}`}>
                    <CustomActionButton state="alt" aria-label="Ir para consulta completa">
                      CONSULTA COMPLETA
                    </CustomActionButton>
                  </a>
                )}
              </div>
            </div>

            <div className="flex flex-row items-center gap-3 justify-between">
              <span
                onClick={copyBrokerIdToClipboard}
                style={{ borderRadius: "100px" }}
                className="relative inline-flex bg-graySuperLight text-blueMediumDark text-sm font-medium px-3 py-1 flex-row items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <span className="font-semibold">
                  CONTA:{" "}
                  <span className="font-normal">{apiClient.brokerId}</span>
                </span>

                <Copy size={14} className="text-blueMediumDark" />

                {accountCopied && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 transform mt-2 bg-blueDarker text-white text-xs px-3 py-1 rounded z-10 whitespace-nowrap">
                    Copiado para a área de transferência!
                    <div className="absolute -top-1 left-1/2 -translate-x-1 transform w-2 h-2 bg-blueDarker rotate-45"></div>
                  </div>
                )}
              </span>

              <span className="font-semibold text-blueMediumDark text-base">
                E-mail:{" "}
                <span className="font-normal">{apiClient.client.email}</span>
              </span>

              <span className="font-semibold text-blueMediumDark text-base">
                CPF/CNPJ:{" "}
                <span className="font-normal">{apiClient.client.cpfCnpj}</span>
              </span>

              <span className="font-semibold text-blueMediumDark text-base">
                Data de Vínculo:{" "}
                <span className="font-normal">{apiClient.client.linkDate}</span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {tabs.map((tab) => (
              <DashboardTab
                key={tab.key}
                label={tab.label}
                active={activeTab === tab.key}
                completed={tab.completed}
                onClick={() => handleTabChange(tab.key)}
                style="small"
              />
            ))}
          </div>

          <div className="h-fit mb-5">
            {activeTab === "investment_funds" && (
              <InvestmentFunds
                apiClientId={apiClient.id}
                onPositionUpdated={fetchData}
              />
            )}
            {activeTab === "fixed_income" && (
              <FixedIncome apiClientId={apiClient.id} />
            )}
            {activeTab === "equities" && (
              <Equities
                apiClientId={apiClient.id}
                accountNumber={apiClient.brokerId}
                clientName={apiClient.client.name}
              />
            )}
            {activeTab === "pension" && <Pension apiClientId={apiClient.id} />}
            {activeTab === "statement" && (
              <Statement apiClientId={apiClient.id} />
            )}
            {activeTab === "history" && (
              <History key={apiClient.id} apiClientId={apiClient.id} />
            )}
          </div>
        </div>

        <div className="bg-tealTransparent p-5 rounded-custom20 h-fit flex flex-col gap-5">
          <div className="flex flex-row gap-5">
            <div className="bg-white rounded-custom20 p-5 flex-1 flex flex-col gap-1">
              <div className="flex flex-row items-center gap-1">
                <CircleDotDashed
                  size={16}
                  strokeWidth={2}
                  className="text-tealMedium"
                />
                <span className="text-blueDarker text-sm font-semibold">
                  Situação
                </span>
              </div>

              <span className={`text-lg font-semibold ${statusColor}`}>
                {apiClient.client.tStatus}
              </span>
            </div>

            <div className="bg-white rounded-custom20 p-5 flex-1 flex flex-col gap-1">
              <div className="flex flex-row items-center gap-1">
                <CircleDotDashed
                  size={16}
                  strokeWidth={2}
                  className="text-tealMedium"
                />
                <span className="text-blueDarker text-sm font-semibold">
                  Perfil de Suitability
                </span>
              </div>

              <span className={`text-lg font-semibold ${statusColor}`}>
                {apiClient.client.suitabilityProfile.short}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-custom20 p-5 flex flex-col gap-4">
            <div className="flex flex-row items-center gap-2 justify-between">
              <span className="flex items-center gap-3">
                <Wallet
                  size={20}
                  strokeWidth={1.75}
                  className="text-tealMedium"
                />
                <span className="text-blueDarker text-xl font-semibold">
                  Carteiras
                </span>
              </span>

              <ScoreBadge
                score={generalScore && generalScore.score}
                tone={generalScore && generalScore.tone}
              />
            </div>

            <CustomSelect
              name="walletProfileId"
              label="Perfil de Carteira"
              labelClassName="text-sm font-medium"
              py="py-2"
              options={walletProfileOptions.map((profile) => ({
                label: profile.label,
                value: profile.value,
                colorHex: profile.colorHex,
              }))}
              value={walletForm.walletProfileId}
              onChange={handleWalletChange}
              disabled={walletProfileOptions.length === 0 || loading}
            />

            <div className="flex flex-row gap-4">
              <span className="flex-1 flex items-center gap-3 flex-col">
                <CustomSelect
                  name="investmentWalletId"
                  label="Carteira de Investimento"
                  labelClassName="text-sm font-medium"
                  py="py-2"
                  options={investmentWalletOptions.map((wallet) => ({
                    label: wallet.label,
                    value: wallet.value,
                  }))}
                  value={walletForm.investmentWalletId}
                  onChange={handleWalletChange}
                  disabled={
                    !walletForm.walletProfileId ||
                    investmentWalletOptions.length === 0 ||
                    loading
                  }
                />

                <ScoreBadge
                  score={investmentScore && investmentScore.score}
                  tone={investmentScore && investmentScore.tone}
                />
              </span>

              <span className="flex-1 flex items-center gap-3 flex-col">
                <CustomSelect
                  name="pensionWalletId"
                  label="Carteira de Previdência"
                  labelClassName="text-sm font-medium"
                  py="py-2"
                  options={pensionWalletOptions.map((wallet) => ({
                    label: wallet.label,
                    value: wallet.value,
                  }))}
                  value={walletForm.pensionWalletId}
                  onChange={handleWalletChange}
                  disabled={
                    !walletForm.walletProfileId ||
                    pensionWalletOptions.length === 0 ||
                    loading
                  }
                />

                <ScoreBadge
                  score={pensionScore && pensionScore.score}
                  tone={pensionScore && pensionScore.tone}
                />
              </span>
            </div>
            <CustomSelect
              name="offshoreWalletId"
              label="Carteira Internacional"
              labelClassName="text-sm font-medium"
              py="py-2"
              options={offshoreWalletOptions.map((wallet) => ({
                label: wallet.label,
                value: wallet.value,
              }))}
              value={walletForm.offshoreWalletId}
              onChange={handleWalletChange}
              disabled={!walletForm.walletProfileId || offshoreWalletOptions.length === 0 || loading}
            />
          </div>

          <div className="bg-white rounded-custom20 p-5 flex flex-col gap-4">
            <div className="flex flex-col gap-4 justify-between">
              <span className="flex items-center gap-3">
                <Wallet
                  size={20}
                  strokeWidth={1.75}
                  className="text-tealMedium"
                />
                <span className="text-blueDarker text-xl font-semibold">
                  Distribuição da Carteira
                </span>
              </span>

              <DonutChart3
                data={apiClient.investmentsChartData || []}
                height={220}
              />

              <div
                className="bg-blueLight grid items-center p-4 gap-2"
                style={{
                  borderRadius: "12px",
                  gridTemplateColumns: "16px 120px 1fr",
                }}
              >
                <HandCoins size={16} className="text-tealBase" />

                <span className="text-blueDarker text-base font-semibold">
                  Patrimônio total:
                </span>

                <CurrencyText
                  value={apiClient.plTotal}
                  size="small"
                  className="text-blueDarker font-normal"
                />
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-custom20 p-5 flex flex-col gap-4"
            style={{ height: "200px" }}
          >
            <div className="flex flex-row items-center gap-2 justify-between">
              <span className="flex items-center gap-2">
                <Pin size={20} strokeWidth={1.75} className="text-tealMedium" />
                <span className="text-blueDarker text-xl font-semibold">
                  Notas
                </span>
              </span>

              <SmallToggle
                options={["INVESTIMENTOS", "PREVIDÊNCIA"]}
                selected={selectedNoteType}
                onChange={(value) => {
                  setSelectedNoteType(value);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
